import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import Sheet from './Sheet';
import './AndroidSelectPicker.css';

const isAndroidBrowser = () => (
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')
);

const OPTION_ROW_HEIGHT = 64;
const VIRTUALIZE_AFTER = 80;
const VIRTUAL_OVERSCAN = 5;
const FALLBACK_VIEWPORT_HEIGHT = 480;

const isDropdownSelect = (select) => Boolean(
  select
  && !select.disabled
  && !select.multiple
  && select.size <= 1,
);

const findSelectAtClick = (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const directSelect = target?.closest('select');
  if (isDropdownSelect(directSelect)) return directSelect;

  const label = target?.closest('label');
  if (isDropdownSelect(label?.control)) return label.control;

  // On Android the select itself has pointer events disabled so that the
  // browser cannot launch a second, native popup. Resolve the tap by its
  // coordinates instead, but only within that select's own field container.
  if (!target || target.closest('.gm-sheet__backdrop') || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
    return null;
  }

  // The native select is transparent to hit testing on Android, so its
  // nearest field wrapper is the only subtree we need to inspect. Scanning
  // every select on large pages forces repeated layout reads on each tap.
  const field = target.closest(
    '.gm-wiz__field, .gm-travels__field, .gm-travels__control, .gm-countries__region-filter, .gm-qa__sort, .form-group-admin, .form-group, .field, .setting-card',
  );
  const container = field || target.parentElement;
  const candidates = Array.from(container?.querySelectorAll('select:not([multiple])') || []);
  return candidates.find((select) => {
    if (!isDropdownSelect(select) || !select.parentElement?.contains(target)) return false;
    const rect = select.getBoundingClientRect();
    return rect.width > 0
      && rect.height > 0
      && event.clientX >= rect.left
      && event.clientX <= rect.right
      && event.clientY >= rect.top
      && event.clientY <= rect.bottom;
  }) || null;
};

const getSelectTitle = (select, t) => {
  const ariaLabel = select.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel.trim();

  const labelledBy = select.getAttribute('aria-labelledby');
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent || '')
      .join(' ')
      .trim();
    if (text) return text;
  }

  const associatedLabel = Array.from(select.labels || [])
    .map((label) => label.textContent || '')
    .join(' ')
    .trim();
  if (associatedLabel) return associatedLabel;

  const nearbyLabel = select.closest('.gm-wiz__field, .form-group, .field, .setting-card')
    ?.querySelector('label');
  if (nearbyLabel?.textContent?.trim()) return nearbyLabel.textContent.trim();

  return t('Selecionar opção');
};

const setNativeSelectValue = (select, value) => {
  const valueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype,
    'value',
  )?.set;

  if (valueSetter) valueSetter.call(select, value);
  else select.value = value;

  // Dispatch a real bubbling change event so React's controlled select
  // handlers receive the selection just as they do from a native picker.
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

const AndroidSelectPicker = () => {
  const { t } = useLanguage();
  const [activeSelect, setActiveSelect] = useState(null);
  const [search, setSearch] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const optionsRef = useRef(null);

  useLayoutEffect(() => {
    if (!isAndroidBrowser()) return undefined;

    const handleSelectClick = (event) => {
      const select = findSelectAtClick(event);
      if (!select) return;

      // Cancel the browser's native picker and open exactly one app-owned
      // picker instead.
      event.preventDefault();
      event.stopPropagation();
      setSearch('');
      setActiveSelect(select);
    };

    const handleSelectKeyDown = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const select = target?.closest('select');
      if (!isDropdownSelect(select) || !['Enter', ' ', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      setSearch('');
      setActiveSelect(select);
    };

    document.documentElement.classList.add('gm-android-native-selects-disabled');
    document.addEventListener('click', handleSelectClick, true);
    document.addEventListener('keydown', handleSelectKeyDown, true);
    return () => {
      document.documentElement.classList.remove('gm-android-native-selects-disabled');
      document.removeEventListener('click', handleSelectClick, true);
      document.removeEventListener('keydown', handleSelectKeyDown, true);
    };
  }, []);

  const query = search.trim().toLocaleLowerCase();
  const hasQuery = Boolean(query);
  // Do not walk/map every native option just to open the picker. Some trip
  // catalogues contain thousands of cities; the unfiltered path now touches
  // only the visible rows. The full scan is deferred until the user searches.
  const searchableOptions = useMemo(() => {
    if (!activeSelect || !hasQuery) return null;
    const entries = [];
    const nativeOptions = activeSelect.options;
    for (let index = 0; index < nativeOptions.length; index += 1) {
      const option = nativeOptions[index];
      const label = option.label || option.textContent || option.value;
      entries.push({ index, normalizedLabel: label.toLocaleLowerCase() });
    }
    return entries;
    // Cache the option text across keystrokes. The first search walks the
    // native options once; subsequent keystrokes filter lightweight strings.
  }, [activeSelect, hasQuery]);
  const filteredIndexes = useMemo(() => {
    if (!query || !searchableOptions) return null;
    return searchableOptions
      .filter((option) => option.normalizedLabel.includes(query))
      .map((option) => option.index);
  }, [query, searchableOptions]);

  const optionCount = activeSelect
    ? (filteredIndexes === null ? activeSelect.options.length : filteredIndexes.length)
    : 0;
  const virtualized = optionCount > VIRTUALIZE_AFTER;
  const viewportHeight = optionsRef.current?.clientHeight || FALLBACK_VIEWPORT_HEIGHT;
  const visibleStart = virtualized
    ? Math.max(0, Math.floor(scrollTop / OPTION_ROW_HEIGHT) - VIRTUAL_OVERSCAN)
    : 0;
  const visibleEnd = virtualized
    ? Math.min(optionCount, Math.ceil((scrollTop + viewportHeight) / OPTION_ROW_HEIGHT) + VIRTUAL_OVERSCAN)
    : optionCount;
  const visibleOptions = useMemo(() => {
    if (!activeSelect) return [];
    const nativeOptions = activeSelect.options;
    const visible = [];
    for (let listIndex = visibleStart; listIndex < visibleEnd; listIndex += 1) {
      const index = filteredIndexes === null ? listIndex : filteredIndexes[listIndex];
      const option = nativeOptions[index];
      if (!option) continue;
      visible.push({
        value: option.value,
        label: option.label || option.textContent || option.value,
        disabled: option.disabled || option.parentElement?.disabled || false,
        group: option.parentElement?.tagName === 'OPTGROUP' ? option.parentElement.label : '',
        index,
      });
    }
    return visible;
  }, [activeSelect, filteredIndexes, visibleEnd, visibleStart]);

  const close = useCallback(() => {
    setActiveSelect(null);
    setSearch('');
    setScrollTop(0);
  }, []);

  const choose = (option) => {
    if (!activeSelect || option.disabled) return;
    setNativeSelectValue(activeSelect, option.value);
    close();
  };

  const needsSearch = (activeSelect?.options.length || 0) > 8;

  return (
    <Sheet
      open={Boolean(activeSelect)}
      fast
      instant
      autoFocusFirst={false}
      onClose={close}
      title={activeSelect ? getSelectTitle(activeSelect, t) : ''}
      closeLabel={t('Fechar')}
    >
      {activeSelect && (
        <div className="gm-android-select-picker">
          {needsSearch && (
            <label className="gm-android-select-picker__search">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setScrollTop(0);
                  if (optionsRef.current) optionsRef.current.scrollTop = 0;
                }}
                placeholder={t('Pesquisar opções…')}
                aria-label={t('Pesquisar opções')}
              />
            </label>
          )}
          <div
            ref={optionsRef}
            className="gm-android-select-picker__options"
            role="listbox"
            aria-label={getSelectTitle(activeSelect, t)}
            onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          >
            {virtualized && (
              <div
                className="gm-android-select-picker__spacer"
                aria-hidden="true"
                style={{ height: visibleStart * OPTION_ROW_HEIGHT }}
              />
            )}
            {visibleOptions.map((option, visibleIndex) => (
              <button
                key={`${option.index}-${option.value}`}
                type="button"
                role="option"
                aria-selected={activeSelect.value === option.value}
                aria-posinset={visibleStart + visibleIndex + 1}
                aria-setsize={optionCount}
                disabled={option.disabled}
                className={`gm-android-select-picker__option${option.group ? ' is-grouped' : ''}${activeSelect.value === option.value ? ' is-selected' : ''}`}
                onClick={() => choose(option)}
              >
                {option.group && <span className="gm-android-select-picker__group">{option.group}</span>}
                <span className="gm-android-select-picker__label">{option.label}</span>
                {activeSelect.value === option.value && <Check size={18} aria-hidden="true" />}
              </button>
            ))}
            {virtualized && (
              <div
                className="gm-android-select-picker__spacer"
                aria-hidden="true"
                style={{ height: Math.max(0, optionCount - visibleEnd) * OPTION_ROW_HEIGHT }}
              />
            )}
            {optionCount === 0 && (
              <p className="gm-android-select-picker__empty">{t('Nenhuma opção encontrada')}</p>
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
};

export default AndroidSelectPicker;
